import type { ShiftTodayListItem } from '@/api/shifts';

export type ShiftLocation = {
  position: { lat: number; lng: number };
  shifts: ShiftTodayListItem[];
};

// Bubble radius on screen: a single shift still reads at a glance from the
// whole-country view, and the busiest place is never more than a thumb wide.
export const MIN_BUBBLE_RADIUS_PX = 12;
export const MAX_BUBBLE_RADIUS_PX = 30;

// Shifts closer than this on screen share one bubble: the diameter of the
// largest bubble, so bubbles do not pile up on each other.
const CLUSTER_DISTANCE_PX = MAX_BUBBLE_RADIUS_PX + MAX_BUBBLE_RADIUS_PX;
const TILE_SIZE_PX = 256;
const ZOOM_BASE = 2;
const DEGREES_PER_HALF_TURN = 180;
const DEGREES_PER_TURN = 360;
const RADIANS_PER_DEGREE = Math.PI / DEGREES_PER_HALF_TURN;
const RADIANS_PER_TURN = DEGREES_PER_TURN * RADIANS_PER_DEGREE;

// Ground meters one screen pixel spans at the equator at zoom 0: Earth's
// circumference over the TILE_SIZE_PX-wide world.
const EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156_543.033_92;

function hasCoordinates(shift: ShiftTodayListItem): boolean {
  return typeof shift.latitude === 'number' && typeof shift.longitude === 'number';
}

type Point = { x: number; y: number };

// Web Mercator, the projection Google Maps draws with, in radians: the world is
// RADIANS_PER_TURN wide, so distances here scale to screen pixels with the zoom.
function mercator(shift: ShiftTodayListItem): Point {
  return {
    x: (shift.longitude as number) * RADIANS_PER_DEGREE,
    y: Math.atanh(Math.sin((shift.latitude as number) * RADIANS_PER_DEGREE)),
  };
}

// CLUSTER_DISTANCE_PX in Mercator radians at this zoom: the world is
// TILE_SIZE_PX * 2^zoom pixels wide.
function clusterReach(zoom: number): number {
  return (CLUSTER_DISTANCE_PX * RADIANS_PER_TURN) / (TILE_SIZE_PX * ZOOM_BASE ** zoom);
}

// One bubble per group of shifts that would sit within CLUSTER_DISTANCE_PX of
// each other on screen: far out nearby patients merge, close in each address
// stands alone. Shifts at the same coordinates always share a bubble; shifts
// without coordinates are left out of the map.
export function groupShiftsByLocation(shifts: ShiftTodayListItem[], zoom: number): ShiftLocation[] {
  const reach = clusterReach(zoom);
  const groups: { anchor: Point; shifts: ShiftTodayListItem[] }[] = [];
  shifts.filter(hasCoordinates).forEach((shift) => {
    const point = mercator(shift);
    const near = groups.find(({ anchor }) => Math.hypot(point.x - anchor.x, point.y - anchor.y) <= reach);
    if (near) near.shifts.push(shift);
    else groups.push({ anchor: point, shifts: [shift] });
  });

  return groups.map(({ shifts: group }) => ({
    position: {
      lat: group.reduce((sum, shift) => sum + (shift.latitude as number), 0) / group.length,
      lng: group.reduce((sum, shift) => sum + (shift.longitude as number), 0) / group.length,
    },
    shifts: group,
  }));
}

// Screen radius in pixels. Area grows with the count, so the radius grows with
// its square root.
export function bubbleRadius(count: number, maxCount: number): number {
  if (maxCount <= 1) return MIN_BUBBLE_RADIUS_PX;

  const ratio = Math.sqrt(count) / Math.sqrt(maxCount);

  return MIN_BUBBLE_RADIUS_PX + ratio * (MAX_BUBBLE_RADIUS_PX - MIN_BUBBLE_RADIUS_PX);
}

// Ground meters one screen pixel spans at this zoom and latitude (Web Mercator
// stretches with the latitude), so a circle drawn in meters keeps its screen size.
export function metersPerPixel(zoom: number, lat: number): number {
  return (EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 * Math.cos(lat * RADIANS_PER_DEGREE)) / ZOOM_BASE ** zoom;
}

// A country veil ring as stored: [lat, lng] pairs (see bin/build-country-veils).
export type VeilRing = [number, number][];

// A stored veil ring as a Google Maps path.
export function veilPath(ring: VeilRing): { lat: number; lng: number }[] {
  return ring.map(([lat, lng]) => ({ lat, lng }));
}

const PULSE_DURATION_MS = 4000;
const PULSE_MAX_SCALE = 1.15;
const PULSE_MIN_OPACITY = 0.35;
const PULSE_MAX_OPACITY = 0.55;

export type PulseFrame = { scale: number; opacity: number };

// The ActiveAdmin breath: every 4 s a bubble grows up to 15 % while fading from
// 0.55 to 0.35 opacity, then settles back. sin² keeps both ends of the cycle smooth.
export function pulseFrame(timeMs: number): PulseFrame {
  const sine = Math.sin(((timeMs % PULSE_DURATION_MS) / PULSE_DURATION_MS) * Math.PI);
  const wave = sine * sine;

  return {
    scale: 1 + wave * (PULSE_MAX_SCALE - 1),
    opacity: PULSE_MAX_OPACITY - wave * (PULSE_MAX_OPACITY - PULSE_MIN_OPACITY),
  };
}

// What a bubble looks like when it does not pulse (reduced motion).
export const RESTING_FRAME = pulseFrame(0);
